import { useMutation, useQuery } from '@apollo/client/react';
import {
	CreateFeatureRequestDocument,
	type FeatureRequestObject,
	FeatureRequestsDocument,
	RemoveUpvoteFeatureRequestDocument,
	UpvoteFeatureRequestDocument,
} from '@feature-request/app-graphql';
import type { Feature } from '../types/feature.types';

export const PROJECT_ID =
	import.meta.env.VITE_PROJECT_ID ?? '00000000-0000-0000-0000-000000000000';

export function toFeature(obj: FeatureRequestObject): Feature {
	return {
		id: obj.id,
		title: obj.title,
		description: obj.description ?? null,
		votes: obj.upvoteCount,
		isVoted: obj.isVoted ?? false,
		createdAt: obj.createdAt,
	};
}

export function useFeatureRequests(
	projectId = PROJECT_ID,
	extRefId?: string | null,
) {
	return useQuery(FeatureRequestsDocument, {
		variables: { projectId, extRefId: extRefId ?? undefined },
		skip: !projectId,
	});
}

export function useCreateFeatureRequest(
	projectId = PROJECT_ID,
	extRefId?: string | null,
) {
	return useMutation(CreateFeatureRequestDocument, {
		update(cache, { data }) {
			if (!data?.createFeatureRequest) return;
			if (data.createFeatureRequest.isFlaggedByModeration) return;
			cache.updateQuery(
				{
					query: FeatureRequestsDocument,
					variables: { projectId, extRefId: extRefId ?? undefined },
				},
				(existing) => {
					if (!existing) return existing;
					const newItem = data.createFeatureRequest;
					if (
						existing.featureRequests.some(
							(f) => f.id === newItem.id,
						)
					)
						return existing;
					return {
						featureRequests: [newItem, ...existing.featureRequests],
					};
				},
			);
		},
	});
}

export function useUpvoteFeatureRequest() {
	return useMutation(UpvoteFeatureRequestDocument);
}

export function useRemoveUpvoteFeatureRequest() {
	return useMutation(RemoveUpvoteFeatureRequestDocument);
}
